import { ChevronDown, LayoutDashboard, Calendar as CalendarIcon, Users, Activity, Shield, Eye, FileSignature, Bell, CalendarX2, ClipboardList, BookOpen, Stethoscope, UserCog } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

const itemCls = (active) => `inline-flex items-center gap-2 rounded-lg border-2 px-4 py-2.5 text-sm font-extrabold uppercase tracking-wide transition-colors ${active ? 'border-primary bg-primary/15 text-primary shadow-[0_0_0_1px_rgba(212,175,55,0.35)]' : 'border-primary/40 text-foreground/90 hover:border-primary hover:bg-primary/10 hover:text-primary'}`;

const NavItem = ({ id, icon: Icon, label, active, onSelect, badge, testId }) => (
  <button type="button" onClick={() => onSelect(id)} className={itemCls(active)} data-testid={testId || `nav-${id}`}>
    <Icon className="w-4 h-4" />{label}
    {badge != null && <span className="ml-1 rounded-full bg-primary text-black text-[10px] font-black px-2 py-0.5 leading-none" data-testid={`${testId || `nav-${id}`}-badge`}>{badge}</span>}
  </button>
);

const NavGroup = ({ id, icon: Icon, label, items, activeTab, onSelect }) => {
  const active = items.some(i => i.id === activeTab);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" className={itemCls(active)} data-testid={`nav-group-${id}`}><Icon className="w-4 h-4" />{label}<ChevronDown className="w-3.5 h-3.5 opacity-70" /></button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-[260px] border-primary/40 bg-card/95 backdrop-blur-xl p-1.5" data-testid={`nav-menu-${id}`}>
        {items.map(i => (
          <DropdownMenuItem key={i.id} onSelect={() => onSelect(i.id)} data-testid={`nav-${i.id}`}
            className={`gap-3 rounded-md px-3 py-2.5 text-sm font-semibold cursor-pointer ${activeTab === i.id ? 'bg-primary/15 text-primary' : 'focus:bg-primary/10 focus:text-primary'}`}>
            <i.icon className="w-4 h-4" />
            <span className="flex-1">{i.label}</span>
            {i.hint && <span className="text-[10px] font-normal text-muted-foreground">{i.hint}</span>}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const NavBar = ({ activeTab, onSelect, role, auditCount, cancelledCount }) => {
  const isPhysician = role === 'physician';
  const isManagement = role === 'management';
  const isAdmin = role === 'admin';
  if (isPhysician) {
    return <nav className="mb-6 flex flex-wrap gap-2" data-testid="main-nav"><NavItem id="progressnotes" icon={FileSignature} label="Progress Notes" active={activeTab === 'progressnotes'} onSelect={onSelect} /></nav>;
  }
  const patientItems = [
    { id: 'patients', icon: Users, label: 'Patient Records' },
    { id: 'followups', icon: Bell, label: 'New Patient Follow-up Call' },
    { id: 'cancellations', icon: CalendarX2, label: 'Appointment Cancellation', hint: cancelledCount ? `${cancelledCount}` : undefined },
    { id: 'intake', icon: ClipboardList, label: 'Intake Form' },
  ];
  const clinicalItems = [
    { id: 'services', icon: Activity, label: 'Services' },
    { id: 'education', icon: BookOpen, label: 'Education' },
  ];
  const adminItems = [
    { id: 'staff', icon: Stethoscope, label: 'Staff Members' },
    ...(isAdmin ? [{ id: 'users', icon: UserCog, label: 'User Logins & Roles' }] : []),
  ];
  return (
    <nav className="mb-6 rounded-xl border border-border/60 bg-card/60 p-2 flex flex-wrap gap-2" data-testid="main-nav">
      <NavItem id="dashboard" icon={LayoutDashboard} label="Dashboard" active={activeTab === 'dashboard'} onSelect={onSelect} />
      <NavItem id="calendar" icon={CalendarIcon} label="Calendar" active={activeTab === 'calendar'} onSelect={onSelect} />
      <NavGroup id="patients" icon={Users} label="Patients" items={patientItems} activeTab={activeTab} onSelect={onSelect} />
      {!isManagement && <NavGroup id="clinical" icon={Activity} label="Services & Education" items={clinicalItems} activeTab={activeTab} onSelect={onSelect} />}
      {!isManagement && <NavGroup id="admin" icon={Shield} label="Admin" items={adminItems} activeTab={activeTab} onSelect={onSelect} />}
      {isAdmin && <NavItem id="audit" icon={Eye} label="Audit Logs" active={activeTab === 'audit'} onSelect={onSelect} badge={auditCount?.toLocaleString()} testId="tab-audit" />}
      {!isManagement && <NavItem id="hipaa" icon={Shield} label="HIPAA" active={activeTab === 'hipaa'} onSelect={onSelect} testId="tab-hipaa" />}
      <NavItem id="progressnotes" icon={FileSignature} label="Progress Notes" active={activeTab === 'progressnotes'} onSelect={onSelect} testId="tab-progressnotes" />
    </nav>
  );
};
